import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type Value } from 'react-phone-number-input';

import Modal, { ModalButton, modalInputClass } from '@/components/common/Modal';
import PasswordField from '@/components/common/PasswordField';
import { showToast } from '@/utils';
import { phoneSchema, resetPasswordSchema } from '@/utils/validation';
import { useForgotPasswordMutation, useResetPasswordMutation } from '@/store/api/authApi';
import PhoneInput from '@/components/common/PhoneInput';

type Props = {
  open: boolean;
  onClose: () => void;
};

type PhoneFormValues = z.infer<typeof phoneSchema>;
type ResetFormValues = z.infer<typeof resetPasswordSchema>;

const ForgotPasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [forgotPassword, { isLoading: sendingOtp }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: resetting }] = useResetPasswordMutation();

  const [step, setStep] = useState<'phone' | 'reset'>('phone');
  const [mobile, setMobile] = useState('');

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { mobile: '' },
  });

  const resetForm = useForm<ResetFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { otp: '', newPassword: '', confirmPassword: '' },
  });

  const handleClose = () => {
    setStep('phone');
    setMobile('');
    phoneForm.reset();
    resetForm.reset();
    onClose();
  };

  const onSendOtp = async (data: PhoneFormValues) => {
    try {
      await forgotPassword({ mobile: data.mobile }).unwrap();
      setMobile(data.mobile);
      setStep('reset');
      showToast('OTP sent to your mobile number', 'success');
    } catch (error: any) {
      showToast(error?.data?.message || 'Failed to send OTP', 'error');
    }
  };

  const onReset = async (data: ResetFormValues) => {
    try {
      await resetPassword({
        mobile,
        newPassword: data.newPassword,
        code: data.otp,
      }).unwrap();
      showToast('Password reset successfully, please login', 'success');
      handleClose();
    } catch (error: any) {
      showToast(error?.data?.message || 'Failed to reset password', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Forgot Password"
      description={
        step === 'phone'
          ? "Enter your registered mobile number and we'll send you a verification code."
          : `Enter the code sent to ${mobile} and choose a new password.`
      }
      size="sm"
    >
      {step === 'phone' && (
        <form onSubmit={phoneForm.handleSubmit(onSendOtp)} className="space-y-4">
          <div>
            <Controller
              name="mobile"
              control={phoneForm.control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value as Value}
                  onChange={(phoneData) => field.onChange(phoneData.fullNumber)}
                />
              )}
            />
            {phoneForm.formState.errors.mobile && (
              <p className="mt-1 text-xs text-danger-red">
                {phoneForm.formState.errors.mobile.message}
              </p>
            )}
          </div>
          <ModalButton type="submit" disabled={sendingOtp} className="h-11 w-full">
            {sendingOtp ? 'Sending...' : 'Send OTP'}
          </ModalButton>
        </form>
      )}

      {step === 'reset' && (
        <form onSubmit={resetForm.handleSubmit(onReset)} className="space-y-4">
          <div>
            <input
              {...resetForm.register('otp')}
              placeholder="Verification code"
              inputMode="numeric"
              className={modalInputClass}
            />
            {resetForm.formState.errors.otp && (
              <p className="mt-1 text-xs text-danger-red">
                {resetForm.formState.errors.otp.message}
              </p>
            )}
          </div>
          <PasswordField
            placeholder="New password"
            {...resetForm.register('newPassword')}
            error={resetForm.formState.errors.newPassword?.message}
          />
          <PasswordField
            placeholder="Confirm new password"
            {...resetForm.register('confirmPassword')}
            error={resetForm.formState.errors.confirmPassword?.message}
          />
          <ModalButton type="submit" disabled={resetting} className="h-11 w-full">
            {resetting ? 'Resetting...' : 'Reset Password'}
          </ModalButton>
        </form>
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
